import { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { useSelector } from '../context/hooks';
import { selectMeter, selectStepIndex } from '../context/selectors';

import { METER_STEPPER_COUNT } from './stepper-utils';

export interface StepperProps {
	/**
	 * Limit how many steps are shown in the stepper.
	 * Useful on early steps (usage, needs) where only the first three
	 * steps are relevant to the user's current journey.
	 * Defaults to showing all steps.
	 */
	visibleStepCount?: number;
}

export const Stepper = ({ visibleStepCount }: StepperProps = {}) => {
	const currentIndex = useSelector(selectStepIndex);
	const meter = useSelector(selectMeter);
	const intl = useIntl();

	// When visibleStepCount is not explicitly passed, derive it from the meter.
	// csResolutions skips the allowance step so only 3 steps are ever shown;
	// all other meters show either a capped preview (when called with an
	// explicit visibleStepCount from usage/needs steps) or all 5.
	const resolvedVisibleStepCount = visibleStepCount ?? METER_STEPPER_COUNT[meter];

	const navSteps = useMemo(
		() => [
			intl.formatMessage({
				id: 'ubp.calculator.topnav.usage',
				defaultMessage: 'Meter',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.needs',
				defaultMessage: 'Usage needs',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.estimate',
				defaultMessage: 'Estimate',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.allowance',
				defaultMessage: 'Compare',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.summary',
				defaultMessage: 'Summary',
			}),
		],
		[intl],
	);

	const visibleSteps = navSteps.slice(0, resolvedVisibleStepCount);

	return (
		<div css={stepperRowStyle} className={currentIndex > 0 ? 'middle-step' : 'first-step'}>
			{visibleSteps.map((navStep: string, index: number) => {
				return (
					<StepperItem
						key={`step${index}`}
						index={index}
						currentIndex={currentIndex}
						label={navStep}
						isLast={index === visibleSteps.length - 1}
					/>
				);
			})}
		</div>
	);
};

interface StepperItemProps {
	label: string;
	index: number;
	currentIndex: number;
	isLast?: boolean;
}

// eslint-disable-next-line complexity
const StepperItem = (props: StepperItemProps) => {
	const { label, index, currentIndex, isLast } = props;
	const isPast = index < currentIndex;
	const isFuture = index > currentIndex;
	const isCurrent = index === currentIndex;
	const isFirst = index === 0;

	const leftColor = isFirst
		? 'transparent'
		: isCurrent || isPast
			? wacToken('color.primary.blue')
			: wacToken('color.neutral.light400');
	const rightColor = isLast
		? 'transparent'
		: isCurrent || isFuture
			? wacToken('color.neutral.light400')
			: wacToken('color.primary.blue');

	return (
		<div
			css={stepperItemCss}
			style={{
				backgroundImage: `linear-gradient(to right, ${leftColor} 0 50%, ${rightColor} 50% 100%)`,
			}}
		>
			<div css={stepperCircle} className={`active-${isPast || isCurrent}`}>
				{index + 1}
			</div>
			<div className={`active-${isPast || isCurrent}`} css={stepperLabelStyle}>
				{label}
			</div>
		</div>
	);
};

const stepperItemCss = css({
	backgroundSize: '100% 1px',
	backgroundPosition: '0 8px',
	backgroundRepeat: 'no-repeat',
	display: 'flex',
	gap: token('space.100'),
	textAlign: 'center',
	alignItems: 'flex-start',
	justifyContent: 'center',
	flexDirection: 'column',
	width: '100px',
	[media.below.sm]: {
		display: 'none',
	},
});

const stepperRowStyle = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-start',
	width: '100%',
	margin: 'auto',
});

const stepperCircle = css({
	margin: 'auto',
	backgroundColor: wacToken('color.neutral.light200'),
	color: wacToken('color.primary.black'),
	width: '16px',
	height: '16px',
	borderRadius: '50%',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderColor: wacToken('color.neutral.light200'),
	fontSize: 10,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '0 0 auto',
	['&.active-true']: {
		backgroundColor: wacToken('color.primary.blue'),
		borderColor: wacToken('color.primary.blue'),
		color: wacToken('color.neutral.light0'),
	},
});

const stepperLabelStyle = css({
	margin: 'auto',
	fontSize: '0.8rem',
	paddingInline: token('space.100'),
	color: wacToken('color.primary.black'),
});
